import * as yup from '@if/core/yup'
import { type FormSetup, useConfirmDialog, useConfirmDiscardDialog } from '@if/mui'
import { Alert, Divider, Stack } from '@mui/material'
import { useUser } from '@src/contexts/AppContext'
import useFeatureFlags from '@src/data/api/featureSwitch-api/featureSwitch-api'
import { useUpdateMerchant } from '@src/data/api/merchants-api/merchants-api'
import { EBannerOwnedFieldKey, EPromotionMode } from '@src/data/types/Constants'
import { Merchant } from '@src/data/types/Merchant'
import { MerchantPaymentPlan } from '@src/data/types/MerchantPaymentPlan'
import { isEqual } from 'lodash-es'
import { useCallback } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { buildUpdateMerchantDto } from '../buildUpdateMerchantDto'
import CustomPromotionPanel from '../CustomPromotionPanel'
import { MerchantPlanPromoRulesSchema } from '../MerchantPromoPlanPanel'
import MerchantPromotionPanel from '../MerchantPromotionPanel'
import PromotionModeSelector from '../PromotionModeSelector'
import { MerchantTabConfig, TabFormMethods } from './MerchantGeneralTab'

export const MerchantPromotionFormSchema = MerchantPlanPromoRulesSchema.concat(
  yup.object().shape({
    promotionMode: yup.mixed<EPromotionMode>().required().default(EPromotionMode.Preconfigured),
    // Custom promotion notification recipients: committing to custom mode requires
    // at least one address on file before the tab can be saved.
    promoNotificationEmailList: yup
      .array()
      .of(yup.string().email().required())
      .required()
      .when('promotionMode', {
        is: EPromotionMode.Custom,
        then: (schema) => schema.min(1),
      })
      .default([]),
    // Custom Buydown rate rebate; the per-user cap is supplied via the $maxRebate form context.
    flashPlanRateRebate: yup.percent().min(0).max(yup.ref('$maxRebate')).required().default(0),
  }),
)

type MerchantPromotionFormSchemaType = typeof MerchantPromotionFormSchema

export type MerchantPromotionFormValues = yup.InferSubmit<MerchantPromotionFormSchemaType>

interface MerchantPromotionTabContentProps {
  merchant: Merchant
  merchantPaymentPlans: MerchantPaymentPlan[]
  isPaymentPlanFetching: boolean
}

const MerchantPromotionTabContent = ({
  merchant,
  merchantPaymentPlans,
  isPaymentPlanFetching,
}: MerchantPromotionTabContentProps) => {
  const { t } = useTranslation()
  const [featureFlags] = useFeatureFlags()
  const { control, getValues, setValue, resetField } = useFormContext<MerchantPromotionFormValues>()
  const promotionMode = useWatch({ control, name: 'promotionMode' })

  const confirmModeSwitch = useConfirmDiscardDialog({
    title: t('merchant.promotionMode.switchConfirmTitle'),
    message: (
      <Stack gap={2}>
        <span>{t('merchant.promotionMode.switchConfirmMessage')}</span>
        <Divider />
        <span>{t('merchant.promotionMode.customWarningMessage')}</span>
      </Stack>
    ),
  })

  const confirmCustomMode = useConfirmDialog({
    title: t('merchant.promotionMode.customWarningTitle'),
    message: t('merchant.promotionMode.customWarningMessage'),
  })

  const confirmLeaveCustom = useConfirmDiscardDialog({
    title: t('merchant.promotionMode.switchConfirmTitle'),
    message: t('merchant.promotionMode.switchConfirmMessage'),
  })

  const handleModeChange = (previousMode: EPromotionMode) => {
    if (previousMode === EPromotionMode.Custom) {
      const hasUnsavedChanges = !isEqual(
        getValues('promoNotificationEmailList'),
        merchant.promoNotificationEmailList ?? [],
      )
      if (!hasUnsavedChanges) {
        return
      }

      void confirmLeaveCustom.open().then((discard) => {
        if (discard) {
          resetField('promoNotificationEmailList')
        } else {
          setValue('promotionMode', previousMode, { shouldDirty: true })
        }
      })
      return
    }

    // Flipping to Custom invalidates the Flash/Elite/Flex plans, so it is always gated.
    // Only the preconfigured side holds these unsaved edits in the form (same change
    // detection as onSubmit); they add the discard notice to the warning.
    const hasUnsavedChanges = !isEqual(getValues('planPromoRules'), merchant.planPromoRules)

    const confirmation = hasUnsavedChanges ? confirmModeSwitch : confirmCustomMode
    void confirmation.open().then((confirmed) => {
      if (confirmed) {
        // Leaving a mode abandons its in-form edits: drop them back to their defaults.
        resetField('planPromoRules')
      } else {
        setValue('promotionMode', previousMode, { shouldDirty: true })
      }
    })
  }

  // When the parent banner owns the buydown, the merchant can only view the inherited plan:
  // the mode selector and buydown editor are locked.
  const isBuydownBannerOwned = (merchant.bannerOwnedFields ?? []).includes(EBannerOwnedFieldKey.CustomBuydown)

  const canEditRebate = (useUser()?.buyDownRebateMax ?? 0) > 0

  return (
    <Stack gap={3}>
      {featureFlags?.useCustomFlashPlan && (
        <PromotionModeSelector
          control={control}
          name="promotionMode"
          onChange={isBuydownBannerOwned ? undefined : () => handleModeChange(promotionMode)}
          disabled={isBuydownBannerOwned}
        />
      )}
      {isBuydownBannerOwned && <Alert severity="info">{t('merchant.customBuydown.managedByBanner')}</Alert>}
      {featureFlags?.useCustomFlashPlan && promotionMode === EPromotionMode.Custom ? (
        <CustomPromotionPanel id={merchant.id} readOnly={isBuydownBannerOwned} rebateReadOnly={!canEditRebate} />
      ) : (
        <MerchantPromotionPanel
          merchant={merchant}
          isPaymentPlanFetching={isPaymentPlanFetching}
          merchantPaymentPlans={merchantPaymentPlans}
          planPromoRules={getValues('planPromoRules')}
        />
      )}
    </Stack>
  )
}

/**
 * Builds the Promotion tab's form config and body. Saves payment plans (separate
 * endpoint) and/or the promo-rules slice, depending on what changed.
 */
export function useMerchantPromotionTab(
  merchant: Merchant,
  merchantPaymentPlans: MerchantPaymentPlan[],
  isPaymentPlanFetching: boolean,
): MerchantTabConfig<MerchantPromotionFormSchemaType> {
  const [updateMerchant] = useUpdateMerchant()
  const maxRebate = useUser()?.buyDownRebateMax ?? 0

  const useFormSetup = useCallback(
    (): FormSetup<MerchantPromotionFormSchemaType> => ({
      schema: MerchantPromotionFormSchema,
      // $maxRebate caps flashPlanRateRebate. View-only users (max 0) fall back to the stored
      // value as ceiling so an unrelated tab save isn't blocked by a pre-existing rebate.
      context: { maxRebate: maxRebate || (merchant.flashPlanRateRebate ?? 0) },
      defaultValues: {
        planPromoRules: merchant.planPromoRules,
        promotionMode: merchant.flashPlanDefinedInManageMerchant ? EPromotionMode.Custom : EPromotionMode.Preconfigured,
        promoNotificationEmailList: merchant.promoNotificationEmailList ?? [],
        flashPlanRateRebate: merchant.flashPlanRateRebate ?? 0,
      },
    }),
    [
      maxRebate,
      merchant.planPromoRules,
      merchant.flashPlanDefinedInManageMerchant,
      merchant.promoNotificationEmailList,
      merchant.flashPlanRateRebate,
    ],
  )

  const onSubmit = useCallback(
    async (values: MerchantPromotionFormValues, methods: TabFormMethods<MerchantPromotionFormSchemaType>) => {
      const promises: Promise<unknown>[] = []

      // The selected promotion mode is persisted as FlashPlanDefinedInManageMerchant:
      // Custom = true (flash plans defined here, shielded from the Alis sync), Preconfigured = false.
      const flashPlanDefinedInManageMerchant = values.promotionMode === EPromotionMode.Custom
      const modeChanged = flashPlanDefinedInManageMerchant !== merchant.flashPlanDefinedInManageMerchant
      if (
        modeChanged ||
        !isEqual(values.planPromoRules, merchant.planPromoRules) ||
        !isEqual(values.promoNotificationEmailList, merchant.promoNotificationEmailList ?? []) ||
        values.flashPlanRateRebate !== (merchant.flashPlanRateRebate ?? 0)
      ) {
        promises.push(
          updateMerchant(
            buildUpdateMerchantDto(merchant, {
              planPromoRules: values.planPromoRules,
              flashPlanDefinedInManageMerchant,
              promoNotificationEmailList: values.promoNotificationEmailList,
              flashPlanRateRebate: values.flashPlanRateRebate,
            }),
          ),
        )
      }

      await Promise.all(promises)
      methods.reset(values)
    },
    [merchant, updateMerchant],
  )

  const children = useCallback(
    () => (
      <MerchantPromotionTabContent
        merchant={merchant}
        merchantPaymentPlans={merchantPaymentPlans}
        isPaymentPlanFetching={isPaymentPlanFetching}
      />
    ),
    [merchant, merchantPaymentPlans, isPaymentPlanFetching],
  )

  return { useFormSetup, onSubmit, children }
}
